import React, { useCallback, useMemo, useState } from 'react';
import { batch, useDispatch } from 'react-redux';
import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import {
  Avatar,
  Box,
  ButtonIcon,
  Icon,
  TabGroup,
  TabItem,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../shared/hooks';
import type {
  ActiveAdvancedFilterState,
  RootState,
} from '../../../../shared/types';
import { getBrowserTimezone } from '../../../../shared/utils';
import { auditLogActions } from '../../data-store';
import { useDateFormat } from '../../hooks';
import {
  formatDateByGlobal,
  formatInTimeZoneWithDisplayFormat,
  getDisplayFormatForGlobalDateFormat,
} from '../../utils/utils';

import { CSS_CLASSES, TAB_CONFIG } from './constants';
import type { AuditLogViewerAuditorSmartFiltersProps, TabType } from './types';

import './AuditLogViewerAuditorSmartFilters.css';

// Map activity item IDs to status labels
const STATUS_LABEL_MAP: Record<string, string> = {
  flagged: 'FLAGGED',
  commented: 'COMMENTED',
  resolved: 'RESOLVED',
} as const;

/**
 * AuditLogViewerAuditorSmartFilters Component
 * Smart filters panel for the audit log viewer auditor view.
 * Implements the Smart Filters Expanded design from Figma with tabs and session details.
 */
function AuditLogViewerAuditorSmartFilters({
  className,
  activeTab: controlledActiveTab,
  onTabChange,
  onClose,
  onNavigateToAllLogs,
}: AuditLogViewerAuditorSmartFiltersProps): React.ReactElement {
  const l = useInvertedLocalize();
  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();
  const [internalActiveTab, setInternalActiveTab] =
    useState<TabType>('review-details');

  // Use controlled or uncontrolled state
  const activeTab = controlledActiveTab ?? internalActiveTab;

  const handleTabChange = (value: string) => {
    const newTab = value as TabType;

    if (onTabChange) {
      onTabChange(newTab);
    } else {
      setInternalActiveTab(newTab);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  // Read from Redux
  const requestMetadata = useTypedSelector(
    (s: RootState) => s.aag.auditLog.requestMetadata
  ) as {
    requestorUserDisplayName?: string;
    ffid?: string;
    ffRoleName?: string;
    ticketNumber?: string;
    businessJustification?: string;
    reasonCode?: string;
    requestStartedAt?: string;
    requestEndedAt?: string;
    sourceTimeZone?: string;
    requestId?: number;
    requestedEntitlementDisplayNames?: string[];
    approverUserDisplayName?: string | null;
  } | null;
  const reviewSummary = useTypedSelector(
    (s: RootState) => s.aag.auditLog.requestReviewSummary
  ) as {
    reviewCompletedByDisplayName?: string;
    reviewCompletedAt?: string;
    reviewComment?: string;
    activitySummary?: {
      flagged?: number;
      commented?: number;
      resolved?: number;
    };
  } | null;
  const globalDateFormat = useDateFormat();

  const formatted = useMemo(() => {
    const displayPattern =
      getDisplayFormatForGlobalDateFormat(globalDateFormat);

    // Format reviewedAt with timezone
    const reviewedAtDate =
      reviewSummary?.reviewCompletedAt != null
        ? new Date(reviewSummary.reviewCompletedAt)
        : null;
    const reviewedAtFormatted =
      reviewedAtDate != null && !Number.isNaN(reviewedAtDate.getTime())
        ? formatDateByGlobal(
            reviewSummary?.reviewCompletedAt ?? '',
            globalDateFormat
          )
        : null;
    const timezoneAbbr =
      reviewedAtDate != null && !Number.isNaN(reviewedAtDate.getTime())
        ? getBrowserTimezone(reviewedAtDate)
        : '';
    const reviewedAtWithTimezone =
      reviewedAtFormatted != null && timezoneAbbr !== ''
        ? `${reviewedAtFormatted} (${timezoneAbbr})`
        : reviewedAtFormatted ?? '-';

    let ffidOrRoleDisplay = '-';
    const roleForFfDisplay = requestMetadata?.ffRoleName?.trim() ?? '';

    if (roleForFfDisplay !== '') {
      ffidOrRoleDisplay = roleForFfDisplay;
    } else {
      const fidForFfDisplay = requestMetadata?.ffid?.trim() ?? '';

      if (fidForFfDisplay !== '') {
        ffidOrRoleDisplay = fidForFfDisplay;
      }
    }

    return {
      reviewedBy: reviewSummary?.reviewCompletedByDisplayName?.trim() ?? '-',
      reviewedAt: reviewedAtWithTimezone,
      comments: reviewSummary?.reviewComment?.trim() ?? '-',
      flagged: reviewSummary?.activitySummary?.flagged ?? 0,
      commented: reviewSummary?.activitySummary?.commented ?? 0,
      resolved: reviewSummary?.activitySummary?.resolved ?? 0,
      requestor: requestMetadata?.requestorUserDisplayName?.trim() ?? '-',
      ffidOrRoleDisplay,
      isFfRoleDetail: Boolean(requestMetadata?.ffRoleName?.trim()),
      ticketNumber: requestMetadata?.ticketNumber?.trim() ?? '-',
      businessJustification:
        requestMetadata?.businessJustification?.trim() ?? '-',
      reasonCode: requestMetadata?.reasonCode?.trim() ?? '-',
      timeRange: (() => {
        if (
          !requestMetadata?.requestStartedAt ||
          !requestMetadata.requestEndedAt
        ) {
          return '-';
        }

        const startDate = new Date(requestMetadata.requestStartedAt);
        const browserTZ = getBrowserTimezone(startDate);

        if (!browserTZ) {
          return '-';
        }

        const startFormatted = formatDateByGlobal(
          requestMetadata.requestStartedAt,
          globalDateFormat
        );
        const endFormatted = formatDateByGlobal(
          requestMetadata.requestEndedAt,
          globalDateFormat
        );

        return `${startFormatted} (${browserTZ}) to ${endFormatted} (${browserTZ})`;
      })(),
      timeRangeSource:
        requestMetadata?.requestStartedAt && requestMetadata.requestEndedAt
          ? `${formatInTimeZoneWithDisplayFormat(
              requestMetadata.requestStartedAt,
              requestMetadata.sourceTimeZone ?? 'UTC',
              displayPattern
            )} (${
              requestMetadata.sourceTimeZone?.trim() ?? '-'
            }) to ${formatInTimeZoneWithDisplayFormat(
              requestMetadata.requestEndedAt,
              requestMetadata.sourceTimeZone ?? 'UTC',
              displayPattern
            )} (${requestMetadata.sourceTimeZone?.trim() ?? '-'})`
          : '-',
      requestId: requestMetadata?.requestId ?? '-',
      tcodes:
        (requestMetadata?.requestedEntitlementDisplayNames ?? [])[0]?.trim() ??
        '-',
      approver: requestMetadata?.approverUserDisplayName?.trim() ?? '-',
    };
  }, [requestMetadata, reviewSummary, globalDateFormat]);

  // Common handler for activity item clicks/keyboard navigation
  const handleActivityItemClick = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
    ) => {
      const target = e.currentTarget;
      const dataLabel = target.getAttribute('data-status-label');
      const itemId = target.getAttribute('data-status-id');

      // Read status from data attribute (source of truth), with fallback to STATUS_LABEL_MAP
      const label =
        dataLabel ?? (itemId ? STATUS_LABEL_MAP[itemId] : null) ?? '';

      if (!label) {
        return;
      }

      // Set ONLY the auditLogStatuses filter to avoid stale data from merging
      const nextActive: ActiveAdvancedFilterState[] = [
        {
          value: 'auditLogStatuses',
          method: 'SEARCH',
          options: [{ label, value: label }],
        },
      ];

      batch(() => {
        dispatch(auditLogActions.setCarryOverAuditStatus(label));
        dispatch(auditLogActions.setSuppressInitialFetch(true));
        dispatch(auditLogActions.setActiveFilters(nextActive));
        dispatch(auditLogActions.setActiveTab('all-logs'));
      });

      // Keep the auditor view's local tab state in sync with Redux.
      onNavigateToAllLogs?.();
    },
    [dispatch, onNavigateToAllLogs]
  );

  const renderReviewDetails = () => (
    <Box className={CSS_CLASSES.SESSION}>
      <Box className={CSS_CLASSES.CONTENT}>
        {/* Activity Summary */}
        <Box className={`${CSS_CLASSES.FIELD} ${CSS_CLASSES.FIELD_FIRST}`}>
          <Typography
            lineLimit={3}
            kind='body3'
            color='--color-foreground-neutral-subtle'
            className={CSS_CLASSES.FIELD_LABEL}>
            {l('Activity Summary')}
          </Typography>

          <Box className={CSS_CLASSES.ACTIVITY_LIST}>
            <Box className={CSS_CLASSES.ACTIVITY_LIST_FRAME}>
              {(() => {
                const items = [
                  {
                    id: 'flagged',
                    icon: 'AlertCriticalOutline',
                    text: `${formatted.flagged} ${
                      formatted.flagged === 1 ? 'item' : 'items'
                    } flagged`,
                  },
                  {
                    id: 'commented',
                    icon: 'Comment',
                    text: `${formatted.commented} ${
                      formatted.commented === 1 ? 'item' : 'items'
                    } commented`,
                  },
                  {
                    id: 'resolved',
                    icon: 'FlagResolved',
                    text: `${formatted.resolved} ${
                      formatted.resolved === 1 ? 'item' : 'items'
                    } flagged and then resolved`,
                  },
                ].filter((item) => {
                  if (item.id === 'flagged') {
                    return Number(formatted.flagged) > 0;
                  }

                  if (item.id === 'commented') {
                    return Number(formatted.commented) > 0;
                  }

                  if (item.id === 'resolved') {
                    return Number(formatted.resolved) > 0;
                  }

                  return false;
                });

                if (items.length === 0) {
                  return (
                    <Box className='AuditLogViewerAuditorSmartFilters-noActivity'>
                      <Typography lineLimit={3} kind='body3'>
                        {l('No Activity Recorded')}
                      </Typography>
                    </Box>
                  );
                }

                return items.map((item) => {
                  const statusLabel = STATUS_LABEL_MAP[item.id];

                  if (!statusLabel) {
                    return null;
                  }

                  return (
                    <div
                      key={item.id}
                      data-status-id={item.id}
                      data-status-label={statusLabel}
                      className={CSS_CLASSES.ACTIVITY_ITEM}
                      role='button'
                      tabIndex={0}
                      onClick={handleActivityItemClick}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          handleActivityItemClick(e);
                        }
                      }}>
                      <Box className='AuditLogViewerAuditorSmartFilters-activityContent'>
                        <Box className={CSS_CLASSES.ACTIVITY_ICON}>
                          <Icon
                            kind={
                              item.icon as React.ComponentProps<
                                typeof Icon
                              >['kind']
                            }
                            size='medium'
                            color={
                              item.id === 'resolved'
                                ? '--color-foreground-success-medium'
                                : '--color-foreground-neutral-medium'
                            }
                          />
                        </Box>
                        <Typography
                          lineLimit={3}
                          kind='body2'
                          color='--color-foreground-secondary-bold'>
                          {item.text}
                        </Typography>
                      </Box>
                      <Box className={CSS_CLASSES.ACTIVITY_ARROW}>
                        <Icon
                          kind='ArrowRight'
                          size='medium'
                          color='--color-foreground-neutral-medium'
                        />
                      </Box>
                    </div>
                  );
                });
              })()}
            </Box>
          </Box>
        </Box>

        {/* Reviewed by */}
        <Box className={CSS_CLASSES.FIELD}>
          <Typography
            lineLimit={3}
            kind='body3'
            color='--color-foreground-neutral-subtle'
            className={CSS_CLASSES.FIELD_LABEL}>
            {l('Reviewed by')}
          </Typography>

          <Box className={CSS_CLASSES.REVIEWER}>
            <Avatar size='xxSmall' />
            <Typography
              lineLimit={3}
              kind='body2'
              color='--color-foreground-secondary-bold'>
              {formatted.reviewedBy || '-'}
            </Typography>
          </Box>
        </Box>

        {/* Review Completed on */}
        <Box className={CSS_CLASSES.FIELD}>
          <Typography
            lineLimit={3}
            kind='body3'
            color='--color-foreground-neutral-subtle'
            className={CSS_CLASSES.FIELD_LABEL}>
            {l('Review Completed on')}
          </Typography>

          <Typography
            lineLimit={3}
            kind='body2'
            color='--color-foreground-secondary-bold'>
            {formatted.reviewedAt || '-'}
          </Typography>
        </Box>

        {/* Comments */}
        <Box className={CSS_CLASSES.FIELD}>
          <Typography
            lineLimit={3}
            kind='body3'
            color='--color-foreground-neutral-subtle'
            className={CSS_CLASSES.FIELD_LABEL}>
            {l('Comments')}
          </Typography>

          <Typography
            lineLimit={3}
            kind='body2'
            color='--color-foreground-secondary-bold'
            className={CSS_CLASSES.COMMENTS}>
            {formatted.comments || '-'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const renderRequestDetails = () => (
    <Box className={CSS_CLASSES.SESSION}>
      <Box className={CSS_CLASSES.CONTENT}>
        {/* Requested by */}
        <Box className={CSS_CLASSES.FIELD}>
          <Typography
            lineLimit={3}
            kind='body3'
            color='--color-foreground-neutral-subtle'
            className={CSS_CLASSES.FIELD_LABEL}>
            {l('User')}
          </Typography>

          <Box className={CSS_CLASSES.REVIEWER}>
            <Avatar size='xxSmall' />
            <Typography
              lineLimit={3}
              kind='body2'
              color='--color-foreground-secondary-bold'>
              {formatted.requestor || '-'}
            </Typography>
          </Box>
        </Box>

        {/* FFID or firefighter role (API shape varies by request type) */}
        <Box className={CSS_CLASSES.FIELD}>
          <Typography
            lineLimit={3}
            kind='body3'
            color='--color-foreground-neutral-subtle'
            className={CSS_CLASSES.FIELD_LABEL}>
            {formatted.isFfRoleDetail ? l('Firefighter Role') : l('FFID')}
          </Typography>
          <Box className={CSS_CLASSES.REVIEWER}>
            <Typography
              lineLimit={3}
              kind='body2'
              color='--color-foreground-secondary-bold'>
              {formatted.ffidOrRoleDisplay || '-'}
            </Typography>
          </Box>
        </Box>

        {/* Ticket Number */}
        <Box className={CSS_CLASSES.FIELD}>
          <Typography
            lineLimit={3}
            kind='body3'
            color='--color-foreground-neutral-subtle'
            className={CSS_CLASSES.FIELD_LABEL}>
            {l('Ticket Number')}
          </Typography>
          <Box className={CSS_CLASSES.REVIEWER}>
            <Typography
              lineLimit={3}
              kind='body2'
              color='--color-foreground-secondary-bold'>
              {formatted.ticketNumber || '-'}
            </Typography>
          </Box>
        </Box>

        {/* Justification (Requestor) */}
        <Box className={CSS_CLASSES.FIELD}>
          <Typography
            lineLimit={3}
            kind='body3'
            color='--color-foreground-neutral-subtle'
            className={CSS_CLASSES.FIELD_LABEL}>
            {l('Justification (Requestor)')}
          </Typography>
          <Box className={CSS_CLASSES.REVIEWER}>
            <Typography
              lineLimit={3}
              kind='body2'
              color='--color-foreground-secondary-bold'>
              {formatted.businessJustification || '-'}
            </Typography>
          </Box>
        </Box>

        {/* Time Requested */}
        <Box className={CSS_CLASSES.FIELD}>
          <Typography
            lineLimit={3}
            kind='body3'
            color='--color-foreground-neutral-subtle'
            className={CSS_CLASSES.FIELD_LABEL}>
            {l('Time Requested')}
          </Typography>
          <Box className={CSS_CLASSES.REVIEWER}>
            <Typography
              lineLimit={3}
              kind='body2'
              color='--color-foreground-secondary-bold'>
              {formatted.timeRange || '-'}
            </Typography>
          </Box>
        </Box>

        {/* Time Requested in Source System Time */}
        <Box className={CSS_CLASSES.FIELD}>
          <Typography
            lineLimit={3}
            kind='body3'
            color='--color-foreground-neutral-subtle'
            className={CSS_CLASSES.FIELD_LABEL}>
            {l('Time Requested in Source System Time')}
          </Typography>
          <Box className={CSS_CLASSES.REVIEWER}>
            <Typography
              lineLimit={3}
              kind='body2'
              color='--color-foreground-secondary-bold'>
              {formatted.timeRangeSource || '-'}
            </Typography>
          </Box>
        </Box>

        {/* Request ID */}
        <Box className={CSS_CLASSES.FIELD}>
          <Typography
            lineLimit={3}
            kind='body3'
            color='--color-foreground-neutral-subtle'
            className={CSS_CLASSES.FIELD_LABEL}>
            {l('Request ID')}
          </Typography>
          <Box className={CSS_CLASSES.REVIEWER}>
            <Typography
              lineLimit={3}
              kind='body2'
              color='--color-foreground-secondary-bold'>
              {formatted.requestId || '-'}
            </Typography>
          </Box>
        </Box>

        {/* T-Codes Requested */}
        <Box className={CSS_CLASSES.FIELD}>
          <Typography
            lineLimit={3}
            kind='body3'
            color='--color-foreground-neutral-subtle'
            className={CSS_CLASSES.FIELD_LABEL}>
            {l('T-Codes Requested')}
          </Typography>
          <Box className={CSS_CLASSES.REVIEWER}>
            <Typography
              lineLimit={3}
              kind='body2'
              color='--color-foreground-secondary-bold'>
              {formatted.tcodes || '-'}
            </Typography>
          </Box>
        </Box>

        {/* Approver */}
        <Box className={CSS_CLASSES.FIELD}>
          <Typography
            lineLimit={3}
            kind='body3'
            color='--color-foreground-neutral-subtle'
            className={CSS_CLASSES.FIELD_LABEL}>
            {l('Approver')}
          </Typography>
          <Box className={CSS_CLASSES.REVIEWER}>
            <Avatar size='xxSmall' />
            <Typography
              lineLimit={3}
              kind='body2'
              color='--color-foreground-secondary-bold'>
              {formatted.approver || '-'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box className={cx(CSS_CLASSES.ROOT, className)}>
      {/* Header with Tabs and Close Button */}
      <Box className={CSS_CLASSES.HEADER}>
        <Box className={CSS_CLASSES.TABS}>
          <TabGroup value={activeTab} onChange={handleTabChange}>
            <TabItem
              label={l(TAB_CONFIG['review-details'].label)}
              value={TAB_CONFIG['review-details'].value}
            />
            <TabItem
              label={l(TAB_CONFIG['request-details'].label)}
              value={TAB_CONFIG['request-details'].value}
            />
          </TabGroup>
        </Box>

        <Box className={CSS_CLASSES.CLOSE_BUTTON}>
          <ButtonIcon
            size='medium'
            kind='ghost'
            icon={<Icon kind='Close' size='medium' />}
            ariaLabel={l('Close panel')}
            onClick={handleClose}
          />
        </Box>
      </Box>

      {/* Body Content */}
      <Box className={CSS_CLASSES.BODY}>
        {activeTab === 'review-details' && renderReviewDetails()}
        {activeTab === 'request-details' && renderRequestDetails()}
      </Box>
    </Box>
  );
}

export default AuditLogViewerAuditorSmartFilters;
